import React, {forwardRef, useRef, useState} from 'react';
import {makeStyles} from "@mui/styles";
import Paper from '@mui/material/Paper';
import {Autocomplete as MuiAutoComplete, CircularProgress, createFilterOptions, TextField} from "@mui/material";
import IconButton from "../button/icon_button";
import {Search} from '@mui/icons-material';
import classnames from "classnames";

const useStyles = makeStyles((theme) => ({
    root: {
        width: props => props.fullWidth ? '100%' : props.width ? props.width : '100%',
        padding: '2px 8px',
        display: 'flex',
        alignItems: 'center',
        color: theme.vars.palette.background.paper,
        boxShadow: 'none',
    },
    input: {
        flex: 1,
        color: theme.vars.palette.background.paper,
    },
    icon: {
        padding: '0.145em',
        width: '1.2em',
        height: '1.2em',
    },
}));


const filter = createFilterOptions();

const oldReason = (reason) => {
    switch (reason) {
        case 'selectOption':
            return 'select-option';
        case 'createOption':
            return 'create-option';
        case 'removeOption':
            return 'remove-option';
        default:
            return '';
    }
};

const SearchAutoComplete = ({
                                className,
                                paper = true,
                                searchIcon = true,
                                disabled,
                                options,
                                placeholder = 'search...',
                                label,
                                ariaLabel = 'input',
                                onSelect,
                                onChange,
                                fullWidth = true,
                                multiple,
                                variant = 'standard',
                                disableCloseOnSelect,
                                selectOnFocus,
                                clearOnBlue,
                                handleHomeEndKeys,
                                freeSolo = true,
                                loading,
                                loadingText = 'loading...',
                                filterOptions,
                                addNewText = (input) => `Add ${input}`,
                                renderOption,
                                inputValue,
                                value,
                                iconButtonProps,
                                width,
                                size,
                                getOptionLabel,
                                ...props
                            }, ref
) => {
    const classes = useStyles({fullWidth, width});
    const [open, setOpen] = useState(false);
    const inputRef = useRef();

    const onSearchPressed = () => {
        if (!open) {
            if (inputRef.current) inputRef.current.focus()
        } else {
            if (inputRef.current) inputRef.current.blur()
        }
        setOpen(prevState => !prevState);
    }

    const defaultFilterOptions = (options, params) => {
        const filtered = filter(options, params);
        if (freeSolo) {
            if (params.inputValue !== '') {
                filtered.push({
                    value: {title: params.inputValue, id: -1},
                    title: addNewText(params.inputValue),
                    id: -1,
                });
            }
        }
        return filtered;
    }

    const _getOptionLabel = (option) => {
        if (getOptionLabel) {
            return getOptionLabel(option) ?? "";
        }
        if (typeof option === 'string') return option;
        // Add "xxx" option created dynamically
        if (option.id === -1) return option.value.title;
        // Regular option
        return option.title;
    }

    const defaultRenderOption = (props, option, state) => {
        const content = renderOption?.(props, option, state) ?? function () {
            if (!option) return '';
            if (typeof option === 'string') return (
                <p className={classnames('mx-2 p-2', props.className)}>{option}</p>
            );
            // Add "xxx" option created dynamically
            if (option.id === -1) return (
                <p className='mx-2 p-2'>
                    {option.value.title ?? option.value.label}
                </p>
            )
            // Regular option
            return <p className='mx-2 p-2'>
                {option.title ?? option.label}
            </p>
        }();
        return (
            <li {...props} key={props.id}>
                {content}
            </li>
        );
    }

    const onSelectInner = (e, value, _reason) => {
        const reason = oldReason(_reason);
        e.stopPropagation();
        e.preventDefault();
        if (reason === 'select-option') {
            if (onSelect) onSelect(e, value);
        }
    }

    const component = (
        <MuiAutoComplete
            disabled={disabled}
            inputValue={inputValue}
            value={value}
            open={open}
            onChange={onSelectInner}
            onOpen={() => setOpen(true)}
            onClose={() => setOpen(false)}
            isOptionEqualToValue={(option, value) => option.name === value.name}
            getOptionLabel={_getOptionLabel}
            options={options}
            loading={loading}
            size={size}
            loadingText={loadingText}
            aria-label={ariaLabel}
            aria-multiline={multiple}
            freeSolo={freeSolo}
            multiple={multiple}
            filterOptions={filterOptions ?? defaultFilterOptions}
            selectOnFocus={selectOnFocus}
            clearOnBlur={clearOnBlue}
            handleHomeEndKeys={handleHomeEndKeys}
            fullWidth={fullWidth}
            disableCloseOnSelect={disableCloseOnSelect}
            renderOption={defaultRenderOption}
            className={className}
            renderInput={(params) => (
                <TextField
                    {...params}
                    size={size}
                    inputRef={inputRef}
                    value={inputValue}
                    variant={variant}
                    placeholder={placeholder}
                    aria-label={ariaLabel}
                    label={label}
                    className={classes.input}
                    onChange={onChange}
                    InputProps={{
                        ...params.InputProps,
                        ...(variant === 'standard' && {
                            disableUnderline: true,
                            className: 'pr-0',
                        }),
                        ...props.InputProps,
                        endAdornment: (
                            <>
                                {
                                    loading
                                        ? <CircularProgress color="primary" size={20}/>
                                        : null
                                }
                                {
                                    props.InputProps?.endAdornment
                                }
                                {
                                    searchIcon &&
                                    <IconButton
                                        ref={ref}
                                        disabled={loading}
                                        onPressed={onSearchPressed}
                                        color={'secondary'}
                                        size={'small'}
                                        {...iconButtonProps}
                                    >
                                        <Search className={classes.icon}/>
                                    </IconButton>
                                }
                            </>
                        ),
                    }}
                />
            )}
        />
    )

    if (paper) {
        return (
            <Paper className={classes.root}>
                {component}
            </Paper>
        );
    }
    return component;
}

export default forwardRef(SearchAutoComplete);
